import { ResizeObserver } from '@juggle/resize-observer';
import { CaretDown, CaretUp } from '@liveauctioneers/hammer-ui-core/icons';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { animated, useSpring } from '@react-spring/web';
import { useState } from 'react';
import useMeasure from 'react-use-measure';

import { InvoiceItem } from '@/types/Invoice';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getImageUrl, GetImageUrlParams } from '@/utils/urls';

import { InvoiceSummaryList } from './components/InvoiceSummaryList';
import { InvoiceSummaryListRow } from './components/InvoiceSummaryListRow';

type Props = {
    className?: string;
    currency: string;
    items: InvoiceItem[];
};

export const InvoiceSummaryItems = ({ className, currency, items }: Props) => {
    const formatMessage = useAppFormatMessage();
    const [collapsed, setCollapsed] = useState(false);

    const [contentContainerRef, { height }] = useMeasure({
        polyfill: ResizeObserver,
    });
    const slideInStyles = useSpring({
        height: collapsed ? 0 : height,
        marginTop: collapsed ? 0 : '16px',
        opacity: collapsed ? 0 : 1,
    });

    return (
        <InvoiceSummaryList className={cn('flex', className)}>
            <BodySecondary
                className="flex cursor-pointer justify-between"
                onClick={() => {
                    setCollapsed(!collapsed);
                }}
            >
                {formatMessage(['checkout', 'invoice_summary', 'itemsInOrder'], {
                    values: { count: items?.length ?? 0 },
                })}
                {collapsed ? (
                    <CaretDown
                        className="inline-flex"
                        color="primary"
                        size="sm"
                        style="fill"
                    />
                ) : (
                    <CaretUp
                        className="inline-flex"
                        color="primary"
                        size="sm"
                        style="fill"
                    />
                )}
            </BodySecondary>
            <animated.div
                className="mx-0 my-sm overflow-hidden smMax:mb-0 smMax:flex-col"
                style={slideInStyles}
            >
                <div ref={contentContainerRef}>
                    {items?.map((item) => {
                        const amount = parseFloat(item.salePrice);
                        if (isNaN(amount)) {
                            return null;
                        }
                        const imageParams: GetImageUrlParams = {
                            catalogId: Number(item.catalogId),
                            height: 40,
                            imageNumber: 1,
                            imageVersion: Number(item.imageVersion),
                            itemId: Number(item.itemId),
                            sellerId: Number(item.sellerId),
                        };
                        return (
                            <div key={item.itemId}>
                                <InvoiceSummaryListRow
                                    amount={{ amount: amount, currency }}
                                    label={
                                        <div className="flex max-w-[90%] gap-xs">
                                            <div className="h-[40px] w-[40px] items-center justify-center bg-background-primary">
                                                <img
                                                    alt={item.title}
                                                    className="h-[40px] w-[40px]"
                                                    src={getImageUrl(imageParams)}
                                                />
                                            </div>
                                            <BodySecondary className="line-clamp-2">{item.title}</BodySecondary>
                                        </div>
                                    }
                                />
                            </div>
                        );
                    })}
                </div>
            </animated.div>
        </InvoiceSummaryList>
    );
};
